import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import SelectAnimated from 'react-select/animated';
import { useDispatch, useSelector } from 'react-redux';
import { getAllPlanes, getAllAnswers, setAnswer, saveAnswer, deleteAnswer } from '../../actions/questionActions';
import { getMicrocredencialesCatalogo } from '../../actions/requestActions';
import { Form, Badge, Button, Modal, Spinner } from 'react-bootstrap';
import { QUESTION_IDS } from '../../enums/questionIds';

const ReconocimientoCreditos = ({ title, additionalQuestions, proposalId }) => {
    const dispatch = useDispatch();
    const plans = useSelector(state => state.question.plans);
    const answers = useSelector(state => state.question.answers);
    const makeAnimated = SelectAnimated();
    const { userInfo } = useSelector(state => state.user);
    const loadingAnswers = useSelector(state => state.question.loading);
    const microcredencialesCatalogo = useSelector(state => state.request.microcredencialesCatalogo);
    const loadingMicrocredenciales = useSelector(state => state.request.loadingMicrocredenciales);

    // ID de la pregunta de tipo (modular/independiente)
    const ID_TIPO = QUESTION_IDS.ID_MICRO;
    const ID_MODULAR = '345732a4-f07f-4ff1-bcb2-f10db7566a48';
    const ID_INDEP = '8524a944-6841-4398-a577-0b4c4d9a72f8';
    // ID para la pregunta de articulación
    const ID_ARTICULACION = QUESTION_IDS.ID_ARTICULACION;

    // Estado local
    const [microType, setMicroType] = useState(null);

    const [careerSelected, setCareerSelected] = useState(null);
    const [planSelected, setPlanSelected] = useState(null);
    const [subjectSelected, setSubjectSelected] = useState(null); 

    const [tags, setTags] = useState([]);

    const [isPlanDisabled, setIsPlanDisabled] = useState(true);
    const [isSubjectsDisabled, setIsSubjectsDisabled] = useState(true);
    const [articula, setArticula] = useState(null);
    const [articulaMicros, setArticulaMicros] = useState([]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [nextMicroType, setNextMicroType] = useState(null);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [loadingTipo, setLoadingTipo] = useState(false);
    const [loadingArticulacion, setLoadingArticulacion] = useState(false);

    // Cargar respuesta del tipo de microcredencial y tags
    useEffect(() => {
        
        const tipoRespuesta = answers[ID_TIPO];
        if (tipoRespuesta) {
            if (tipoRespuesta.answer === ID_MODULAR) {
                setMicroType('modular');
            } else if (tipoRespuesta.answer === ID_INDEP) {
                setMicroType('independiente');
            }
            
            // Cargar tags si existen
            if (tipoRespuesta.tags && tipoRespuesta.tags.length > 0) {
                const formattedTags = tipoRespuesta.tags.map(tag => ({
                    id: tag.Id || tag.id,
                    career: {
                        value: tag.cod_carrera,
                        label: tag.descripcion_carrera
                    },
                    plan: {
                        value: tag.cod_plan,
                        label: tag.descripcion_plan
                    },
                    subject: tag.cod_asignatura ? {
                        value: tag.cod_asignatura,
                        label: tag.descripcion_asignatura
                    } : null
                }));
                setTags(formattedTags);
            } else {
                setTags([]);
            }

            // Cargar microcredenciales articuladas
            const articulacionRespuesta = answers[ID_ARTICULACION];
            
            // Si hay respuesta de articulación, asumimos que la respuesta es "Sí"
            if (articulacionRespuesta) {
                setArticula(true);
                
                // Si es una respuesta múltiple
                if (articulacionRespuesta.multipleAnswers?.length > 0) {
                    const microsSeleccionadas = articulacionRespuesta.multipleAnswers.map(answer => ({
                        value: answer.answer,
                        label: microcredencialesCatalogo.find(m => m.c_codigo_id === answer.answer)?.vc_descripcion || answer.answer
                    }));
                    setArticulaMicros(microsSeleccionadas);
                } 
                // Si es una respuesta individual
                else if (articulacionRespuesta.answer) {
                    const microSeleccionada = {
                        value: articulacionRespuesta.answer,
                        label: microcredencialesCatalogo.find(m => m.c_codigo_id === articulacionRespuesta.answer)?.vc_descripcion || articulacionRespuesta.answer
                    };
                    setArticulaMicros([microSeleccionada]);
                }
            }
        } else {
            if (!loadingAnswers) {
                setMicroType(null);
                setTags([]);
                setArticulaMicros([]);
                setArticula(null);
            }
        }
    }, [answers, loadingAnswers, microcredencialesCatalogo]);

    // Cargar catálogo de microcredenciales
    useEffect(() => {
        dispatch(getMicrocredencialesCatalogo(userInfo));
    }, [dispatch, userInfo, proposalId]);

    const saveTags = async (tagsToSaveParam) => {
        try {
            const tagsToSave = (tagsToSaveParam || tags).map(tag => ({
                id_propuesta: proposalId,
                id_pregunta: ID_TIPO,
                cod_carrera: tag.career.value,
                descripcion_carrera: tag.career.label,
                cod_plan: tag.plan.value,
                cod_asignatura: tag.subject?.value || null,
                descripcion_asignatura: tag.subject?.label || null,
                id_usuario: userInfo.id 
            }));
            
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}microcredenciales/tags/save`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + userInfo.token
                },
                body: JSON.stringify(tagsToSave)
            });

            if (response.ok) {
                // dispatch(getAllAnswers(userInfo, proposalId)); // dejar que el padre lo haga
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error al guardar tags:', error);
            return false;
        }
    };

    const deleteAllTags = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}microcredenciales/tags/delete/${proposalId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': 'Bearer ' + userInfo.token
                }
            });

            if (response.ok) {
                // dispatch(getAllAnswers(userInfo, proposalId)); // dejar que el padre lo haga
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error al eliminar tags:', error);
            return false;
        }
    };

    // Opciones de carrera según tipo
    const careerOptions = useMemo(() => {
        if (!microType) return [];
        const preguntaCarrera = additionalQuestions
            .flatMap(q => q.subpreguntas || [])
            .find(q => q.tipo === 'DropDown');
        return preguntaCarrera?.opciones?.map(op => ({
            value: op.EntityId,
            label: op.descripcion
        })) || [];
    }, [additionalQuestions, microType]);

    const planOptions = useMemo(() => {
        return plans?.map(plan => ({
            value: plan.EntityId,
            label: plan.descripcion || plan.EntityId
        })) || [];
    }, [plans]);

    // Opciones de asignatura (solo para modular)
    const subjectOptions = useMemo(() => {
        const selectedPlan = plans?.find(plan => plan.EntityId === planSelected?.value);
        return selectedPlan?.asignaturas?.map(op => ({
            value: op.EntityId,
            label: op.descripcion
        })) || [];
    }, [plans, planSelected]);

    // Opciones de articulación basadas en el catálogo
    const articulacionOptions = useMemo(() => {
        return microcredencialesCatalogo.map(micro => ({
            value: micro.c_codigo_id,
            label: micro.vc_descripcion
        }));
    }, [microcredencialesCatalogo]);

    // Cuando seleccionás carrera, traé los planes
    useEffect(() => {
        setPlanSelected(null);
        setSubjectSelected(null);
        setIsPlanDisabled(!careerSelected);
        if (careerSelected) {
            dispatch(getAllPlanes(userInfo, careerSelected.value)); 
        }
    }, [careerSelected, dispatch, userInfo]);

    useEffect(() => {
        setSubjectSelected(null);
        setIsSubjectsDisabled(!planSelected);
    }, [planSelected]);

    // Handler para tipo
    const handleTipoChange = (tipo) => {
        if (tags.length > 0) {
            setNextMicroType(tipo);
            setShowConfirmModal(true);
            return;
        }

        setMicroType(tipo);
        setCareerSelected(null);
        setPlanSelected(null);
        setSubjectSelected(null);
        setArticula(null);
        setArticulaMicros([]);

        const currentAnswer = answers[ID_TIPO];
        const answerId = currentAnswer?.answerId || currentAnswer?.Id;

        const answerData = {
            propuesta: { Id: proposalId },
            preguntaAsociada: { Id: ID_TIPO },
            respuesta: tipo === 'modular' ? ID_MODULAR : ID_INDEP,
            observacion: null,
            tieneArchivo: false,
            esDicatamen: false
        };

        if (answerId) {
            answerData.Id = answerId;
        }

        dispatch(setAnswer(ID_TIPO, tipo === 'modular' ? ID_MODULAR : ID_INDEP, null, answerId));
        /*dispatch(saveAnswer(answerData, userInfo, null)).then(() => {
            dispatch(getAllAnswers(userInfo, proposalId));
        });*/
    };

    const addTag = () => {
        if (!careerSelected || !planSelected || (microType === 'modular' && !subjectSelected)) {
            console.warn('Faltan datos requeridos para agregar el tag');
            return;
        }
        const newTag = {
            id: `${careerSelected.value}-${planSelected.value}${subjectSelected ? `-${subjectSelected.value}` : ''}`,
            career: careerSelected,
            plan: planSelected,
            subject: microType === 'modular' ? subjectSelected : null
        };
        if (!tags.find(t => t.id === newTag.id)) {
            const updatedTags = [...tags, newTag];
            setTags(updatedTags);
            saveTags(updatedTags);
        } else {
            setShowDuplicateModal(true);
        }
    };

    const removeTag = (tagId) => {
        const updatedTags = tags.filter(t => t.id !== tagId);
        setTags(updatedTags);
        if (updatedTags.length === 0) {
            deleteAllTags();
        } else {
            saveTags(updatedTags);
        }
    };

    const handleConfirmTipoChange = async () => {
        setShowConfirmModal(false);
        setLoadingTipo(true);
        
        try {
            await deleteAllTags();
            
            setMicroType(nextMicroType);
            setCareerSelected(null);
            setPlanSelected(null);
            setSubjectSelected(null);
            setArticula(null);
            setArticulaMicros([]);
            setTags([]);

            const currentAnswer = answers[ID_TIPO];
            const answerId = currentAnswer?.answerId || currentAnswer?.Id;

            const answerData = {
                propuesta: { Id: proposalId },
                preguntaAsociada: { Id: ID_TIPO },
                respuesta: nextMicroType === 'modular' ? ID_MODULAR : ID_INDEP,
                observacion: null,
                tieneArchivo: false,
                esDicatamen: false
            };

            if (answerId) {
                answerData.Id = answerId;
            }

            dispatch(setAnswer(
                ID_TIPO,
                nextMicroType === 'modular' ? ID_MODULAR : ID_INDEP,
                null,
                answerId
            ));

            await dispatch(saveAnswer(answerData, userInfo, null));
            await dispatch(getAllAnswers(userInfo, proposalId));
        } catch (error) {
            console.error('Error al cambiar tipo:', error);
        } finally {
            setLoadingTipo(false);
        }
    };

    // Modificar el handler de articulación
    const handleArticulaChange = (value) => {
        setArticula(value);
        if (!value) {
            setArticulaMicros([]);
            // Eliminar las respuestas existentes
            const currentAnswers = answers[ID_ARTICULACION];
            if (currentAnswers?.multipleAnswers) {
                currentAnswers.multipleAnswers.forEach(answer => {
                    if (answer.answerId) {
                        dispatch(deleteAnswer(answer.answerId, userInfo));
                    }
                });
            }
        }
    };

    // Modificar el handler de cambio de microcredenciales - SOLO actualiza estado local
    const handleArticulaMicrosChange = async (selectedOptions) => {
        setLoadingArticulacion(true);
        
        const prevValues = articulaMicros.map(opt => opt.value);
        const newValues = (selectedOptions || []).map(opt => opt.value);

        // 2. Detectar agregados y eliminados
        const agregados = newValues.filter(val => !prevValues.includes(val));
        const eliminados = prevValues.filter(val => !newValues.includes(val));

        // 3. Guardar solo los agregados
        for (const value of agregados) {
            const answerData = {
                propuesta: { Id: proposalId },
                preguntaAsociada: { Id: ID_ARTICULACION },
                respuesta: value,
                tieneArchivo: false,
                esDicatamen: false
            };
            await dispatch(saveAnswer(answerData, userInfo, null));
        }

        // 4. Borrar solo los eliminados
        const currentAnswers = answers[ID_ARTICULACION];
        if (currentAnswers?.multipleAnswers) {
            for (const answer of currentAnswers.multipleAnswers) {
                if (eliminados.includes(answer.answer) && answer.answerId) {
                    await dispatch(deleteAnswer(answer.answerId, userInfo));
                }
            }
        } else if (currentAnswers?.answerId && eliminados.includes(currentAnswers.answer)) {
            await dispatch(deleteAnswer(currentAnswers.answerId, userInfo));
        }

        setArticulaMicros(selectedOptions || []);

        await dispatch(getAllAnswers(userInfo, proposalId));
        setLoadingArticulacion(false);
    };

    return (
        <div className="form-check">
            <label className='question-title'>{title}</label>
            {loadingTipo && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spinner animation="border" variant="danger" style={{ width: '4rem', height: '4rem' }} />
                </div>
            )}
            <div className="mt-3" style={{ pointerEvents: loadingTipo ? 'none' : 'auto', opacity: loadingTipo ? 0.5 : 1 }}>
                <Form.Check
                    type="radio"
                    label="Modular"
                    name="tipo"
                    id="modular"
                    onChange={() => handleTipoChange('modular')}
                    checked={microType === 'modular'}
                    disabled={loadingTipo}
                />
                <Form.Check
                    type="radio"
                    label="Independiente"
                    name="tipo"
                    id="independiente"
                    onChange={() => handleTipoChange('independiente')}
                    checked={microType === 'independiente'}
                    disabled={loadingTipo}
                />
            </div>

            {microType && (
                <div className="mt-4">
                    {/* Carrera */}
                    <div className="mb-3">
                        <label className='nombre'>Carrera</label>
                        <Select
                            options={careerOptions}
                            value={careerSelected}
                            onChange={setCareerSelected}
                            placeholder="Seleccione carrera"
                            components={makeAnimated}
                            isDisabled={loadingTipo}
                        />
                    </div>
                    {/* Plan */}
                    <div className="mb-3">
                        <label className='nombre'>Plan</label>
                        <Select
                            options={planOptions}
                            value={planSelected}
                            onChange={setPlanSelected}
                            placeholder="Seleccione plan"
                            components={makeAnimated}
                            isDisabled={isPlanDisabled || loadingTipo}
                        />
                    </div>
                    {/* Asignatura solo para modular */}
                    {microType === 'modular' && (
                        <div className="mb-3">
                            <label className='nombre'>Asignatura</label>
                            <Select
                                options={subjectOptions}
                                value={subjectSelected}
                                onChange={setSubjectSelected}
                                placeholder="Seleccione asignatura"
                                components={makeAnimated}
                                isDisabled={isSubjectsDisabled || loadingTipo}
                            />
                        </div>
                    )}
                    <Button
                        variant="primary"
                        onClick={addTag}
                        disabled={
                            !careerSelected ||
                            !planSelected ||
                            (microType === 'modular' && !subjectSelected) ||
                            loadingTipo
                        }
                        className="mb-3"
                    >
                        Agregar
                    </Button>
                    {/* Tags */}
                    <div className="mb-4">
                        {tags.map(tag => (
                            <Badge pill bg="secondary" className="me-2" key={tag.id}>
                                {tag.career.value}-{tag.plan.value}
                                {tag.subject ? `-${tag.subject.value}- ${tag.subject.label}` : ''}
                                <span
                                    onClick={() => removeTag(tag.id)}
                                    style={{ cursor: 'pointer', marginLeft: '10px' }}
                                >×</span>
                            </Badge>
                        ))}
                    </div>
                    {/* Articulación modular */}
                    {microType === 'modular' && (
                        <div className="mb-3 mt-4">
                            <label className='nombre'>¿Articula con otras microcredenciales?</label>
                            <div>
                                <Form.Check
                                    type="radio"
                                    label="Sí"
                                    name="articula"
                                    id="articula_si"
                                    onChange={() => handleArticulaChange(true)}
                                    checked={articula === true}
                                    disabled={loadingTipo}
                                />
                                <Form.Check
                                    type="radio"
                                    label="No"
                                    name="articula"
                                    id="articula_no"
                                    onChange={() => handleArticulaChange(false)}
                                    checked={articula === false}
                                    disabled={loadingTipo}
                                />
                            </div>
                        </div>
                    )}
                    {/* Dropdown múltiple si articula === true */}
                    {microType === 'modular' && articula === true && (
                        <div className="mb-3 mt-2">
                            <label className='nombre'>Seleccione microcredenciales articuladas</label>
                            <Select
                                isMulti
                                options={articulacionOptions}
                                value={articulaMicros}
                                onChange={handleArticulaMicrosChange}
                                placeholder="Seleccione microcredenciales"
                                components={makeAnimated}
                                isDisabled={loadingTipo || loadingMicrocredenciales || loadingArticulacion}
                                isLoading={loadingMicrocredenciales || loadingArticulacion}
                                 theme={theme => ({
                                    ...theme,
                                    colors: {
                                        ...theme.colors,
                                        primary25: '#F20A61', // hover de opción
                                        primary: '#F20A61',   // color principal (incluye spinner)
                                    },
                                })}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Modal de confirmación de cambio de tipo */}
            <Modal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                centered
                backdrop="static"
            >
                <Modal.Header style={{ background: '#F20A61', color: 'white', border: 'none' }}>
                    <Modal.Title>¡Atención!</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ textAlign: 'center', fontSize: '1.1rem' }}>
                    Al cambiar el tipo de microcredencial se eliminará toda la información asociada.<br />
                    ¿Desea continuar?   
                </Modal.Body>
                <Modal.Footer style={{ border: 'none', justifyContent: 'center' }}>
                    <Button
                        variant="secondary"
                        style={{ borderRadius: '20px', minWidth: '100px' }}
                        onClick={() => setShowConfirmModal(false)}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        style={{ background: '#F20A61', border: 'none', borderRadius: '20px', minWidth: '100px' }}
                        onClick={handleConfirmTipoChange}
                    >
                        Sí, continuar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de error por tag duplicado */}
            <Modal
                show={showDuplicateModal}
                onHide={() => setShowDuplicateModal(false)}
                centered
            >
                <Modal.Header style={{ background: '#F20A61', color: 'white', border: 'none' }}>
                    <Modal.Title>Tag duplicado</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ textAlign: 'center', fontSize: '1.1rem' }}>
                    Este tag ya fue agregado. No se permiten duplicados.
                </Modal.Body>
                <Modal.Footer style={{ border: 'none', justifyContent: 'center' }}>
                    <Button
                        variant="primary"
                        style={{ background: '#F20A61', border: 'none', borderRadius: '20px', minWidth: '100px' }}
                        onClick={() => setShowDuplicateModal(false)}
                    >
                        Aceptar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ReconocimientoCreditos;
